/**
 * RespostasFormularioBlock — o que o cliente respondeu nos formularios
 * (FORM-18), com o rotulo do momento do envio e a campanha (UTM).
 *
 * Le `GET /api/crm/leads/{id}/respostas-formulario` (lead:view): quem atende
 * nao precisa de `formulario:view` nem de procurar o lead na aba Respostas.
 * Os arquivos enviados ja sao anexos do lead — aparecem no bloco Anexos.
 */
import React, { useCallback, useEffect, useState } from "react";
import { FiClipboard } from "react-icons/fi";

import Block from "../../../components/Block";
import { useAuth } from "../../../context/AuthContext";
import { listarRespostasDoLead } from "../../../services/formularios";
import {
  formatarValorResposta,
  rotuloEfeitoLead,
  rotuloOrigem,
  textoCruResposta,
} from "../../Formularios/respostasFormat";
import { EmptyMsg, ErrorMsg } from "../../Orcamentos/styles";

export const ID_BLOCO_RESPOSTAS_FORMULARIO = "respostas-formulario";

function quando(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("pt-BR");
}

function utmEmTexto(utm) {
  return Object.entries(utm || {})
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${v}`)
    .join(" · ");
}

function RespostasFormularioBlock({ leadId, versao = 0, collapsible, defaultCollapsed }) {
  const { request } = useAuth();
  const [respostas, setRespostas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const payload = await listarRespostasDoLead(request, leadId, { silentError: true });
      setRespostas(payload?.respostas || []);
      setErro(null);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar as respostas.");
    } finally {
      setCarregando(false);
    }
  }, [request, leadId]);

  // `versao` muda quando o vendedor responde pelo painel: recarrega sem
  // remontar o cockpit.
  useEffect(() => {
    if (leadId) carregar();
  }, [leadId, versao, carregar]);

  return (
    <Block
      id={ID_BLOCO_RESPOSTAS_FORMULARIO}
      collapsible={collapsible}
      defaultCollapsed={defaultCollapsed}
      title={
        <>
          <FiClipboard
            size={13}
            style={{ verticalAlign: "middle", marginRight: 4 }}
            aria-hidden="true"
          />
          Respostas de formulário ({respostas.length})
        </>
      }
    >
      {erro && <ErrorMsg>{erro}</ErrorMsg>}
      {!erro && !carregando && respostas.length === 0 && (
        <EmptyMsg style={{ padding: 8 }}>Nenhum formulário respondido.</EmptyMsg>
      )}
      {respostas.map((resposta) => (
        <article
          key={resposta.id}
          aria-label={`Resposta do formulário ${resposta.formulario_nome}`}
          style={{ padding: "8px 0", borderBottom: "1px solid var(--border)" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <strong>{resposta.formulario_nome}</strong>
            <span className="tiny muted">
              {quando(resposta.created_at)} · {rotuloOrigem(resposta.origem_submissao)}
              {resposta.efeito_lead && resposta.efeito_lead !== "NENHUM"
                ? ` · ${rotuloEfeitoLead(resposta.efeito_lead)}`
                : ""}
            </span>
          </div>
          <dl style={{ margin: "6px 0 0", display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 2fr)", gap: "2px 8px" }}>
            {resposta.campos.map((campo) => {
              const exibe = formatarValorResposta(campo, campo.valor);
              return (
                <React.Fragment key={campo.campo_id}>
                  <dt className="tiny muted" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {campo.label || campo.tipo}
                  </dt>
                  <dd
                    style={{ margin: 0, overflow: "hidden", textOverflow: "ellipsis" }}
                    title={textoCruResposta(campo.valor) || undefined}
                  >
                    {exibe}
                  </dd>
                </React.Fragment>
              );
            })}
          </dl>
          {resposta.anexos.length > 0 && (
            <p className="tiny muted" style={{ margin: "6px 0 0" }}>
              {resposta.anexos.length === 1
                ? "1 arquivo enviado"
                : `${resposta.anexos.length} arquivos enviados`}{" "}
              — veja em Anexos.
            </p>
          )}
          {utmEmTexto(resposta.utm) && (
            <p className="tiny muted" style={{ margin: "4px 0 0" }}>
              Campanha: {utmEmTexto(resposta.utm)}
            </p>
          )}
        </article>
      ))}
    </Block>
  );
}

export default RespostasFormularioBlock;
